// src/communaute/normalize.js
//
// Transforme les réponses JSON de Laravel en objets simples pour React.
// Plusieurs noms de champs sont acceptés (snake_case / camelCase) :
// si un champ s'affiche vide, c'est ici qu'il faut ajouter le bon nom.

const bool = (v) => v === true || v === 1 || v === '1' || v === 'true';
const int = (v) => Number.parseInt(v ?? 0, 10) || 0;

// ══════════════════════════════════════════════════════
//  DATES
// ══════════════════════════════════════════════════════
export function parseDate(v) {
  if (!v) return null;
  if (v instanceof Date) return v;
  const s = typeof v === 'string' && v.includes(' ') && !v.includes('T') ? v.replace(' ', 'T') : v;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "à l'instant" · "5 min" · "3 h" · "2 j" · "12 sept." */
export function tempsRelatif(v) {
  const d = parseDate(v);
  if (!d) return '';
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "à l'instant";
  if (s < 3600) return `${Math.floor(s / 60)} min`;
  if (s < 86400) return `${Math.floor(s / 3600)} h`;
  if (s < 604800) return `${Math.floor(s / 86400)} j`;
  return d.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    ...(d.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}),
  });
}

export function heure(v) {
  const d = parseDate(v);
  return d ? d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
}

/** Aujourd'hui → "14:32" · cette semaine → "lun." · sinon → "12/09" */
export function heureCourte(v) {
  const d = parseDate(v);
  if (!d) return '';
  const maintenant = new Date();
  if (d.toDateString() === maintenant.toDateString()) return heure(d);
  if (maintenant - d < 6 * 86400000) return d.toLocaleDateString('fr-FR', { weekday: 'short' });
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}

/** Retire le HTML éventuel et garde les retours à la ligne (sans exécuter de script) */
export function texteBrut(html) {
  if (!html) return '';
  if (!/<[a-z][\s\S]*>/i.test(html)) return html.trim();
  const avecSauts = html.replace(/<br\s*\/?>/gi, '\n').replace(/<\/(p|div|li)>/gi, '\n');
  const doc = new DOMParser().parseFromString(avecSauts, 'text/html');
  return (doc.body.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
}

// ══════════════════════════════════════════════════════
//  UTILISATEUR
// ══════════════════════════════════════════════════════
export function normUser(u = {}) {
  u = u || {};
  const role = u.role ?? u.pivot?.role;
  return {
    id: int(u.id),
    nom: u.nomComplet ?? u.nom_complet ?? u.name ?? 'Utilisateur',
    photo: u.photo_profil ?? u.photoProfil ?? u.photo ?? null,
    isAbonne: bool(u.is_abonne),
    isFollowed: bool(u.is_followed),
    nbrAbonnes: int(u.nbr_abonnes),
    estMoi: bool(u.is_me ?? u.est_moi),
    estAdmin: role === 'admin' || bool(u.est_admin ?? u.is_admin),
  };
}

// ══════════════════════════════════════════════════════
//  PUBLICATION
// ══════════════════════════════════════════════════════
function listeImages(v) {
  let imgs = v ?? [];
  if (typeof imgs === 'string') imgs = imgs.split('#');
  if (!Array.isArray(imgs)) return [];
  return imgs
    .map((i) => (typeof i === 'string' ? i : i?.url ?? i?.path ?? i?.image))
    .filter((s) => typeof s === 'string' && s.trim() && s !== 'empty')
    .map((s) => s.trim());
}

export function normPost(p = {}) {
  p = p || {};
  return {
    id: int(p.id),
    auteur: normUser(p.auteur ?? p.user ?? {}),
    titre: p.titre ?? '',
    contenu: p.contenu ?? '',
    images: listeImages(p.images ?? p.images_list ?? p.imgs),
    liens: {
      youtube: p.lien_youtube || null,
      facebook: p.lien_facebook || null,
      tiktok: p.lien_tiktok || null,
      instagram: p.lien_instagram || null,
    },
    isLiked: bool(p.is_liked),
    nbrLikes: int(p.nbr_likes ?? p.likes_count),
    nbrComments: int(p.nbr_comments ?? p.comments_count),
    isFavory: bool(p.is_favory),
    isMine: bool(p.is_mine),
    date: p.created_at ?? null,
    dateTexte: p.date_courte ?? p.temps_relatif ?? null,
  };
}

export function normComment(c = {}) {
  c = c || {};
  return {
    id: int(c.id),
    auteur: normUser(c.auteur ?? c.user ?? {}),
    contenu: c.contenu ?? '',
    isMine: bool(c.is_mine),
    date: c.created_at ?? null,
    dateTexte: c.temps_relatif ?? null,
    nbrReplies: int(c.nbr_replies ?? c.replies_count),
    replies: Array.isArray(c.replies) ? c.replies.map(normComment) : [],
  };
}

// ══════════════════════════════════════════════════════
//  MESSAGERIE
// ══════════════════════════════════════════════════════
export function normMessage(m = {}) {
  m = m || {};
  const ev = m.evenement;
  const po = m.post;
  const type = m.type ?? 'texte';
  return {
    id: int(m.id),
    type,
    contenu: m.contenu ?? '',
    sender: normUser(m.sender ?? m.user ?? {}),
    isMine: bool(m.is_mine),
    estSysteme: type === 'systeme' || bool(m.est_systeme),
    date: m.created_at ?? null,
    evenement: ev
      ? {
          id: int(ev.id),
          nom: ev.nomEven ?? ev.nom ?? '',
          image: listeImages(ev.imgEven ?? ev.image)[0] ?? null,
          ville: ev.villeEven ?? ev.ville ?? null,
        }
      : null,
    post: po
      ? {
          id: int(po.id),
          titre: po.titre ?? '',
          image: po.image ?? listeImages(po.images)[0] ?? null,
        }
      : null,
  };
}

export function normConversation(c = {}) {
  c = c || {};
  const participants = (c.participants ?? []).map(normUser);
  const membres = c.membres ? c.membres.map(normUser) : null;
  const dernier = c.dernier_message ? normMessage(c.dernier_message) : null;
  const estGroupe = c.type === 'groupe' || bool(c.est_groupe);
  const autre = participants.find((p) => !p.estMoi);

  return {
    id: int(c.id),
    titre: c.titre ?? c.nom ?? autre?.nom ?? 'Conversation',
    photo: c.photo ?? c.photo_url ?? (!estGroupe ? autre?.photo : null) ?? null,
    estGroupe,
    nonLus: int(c.non_lus),
    muet: bool(c.muet),
    archive: bool(c.archive ?? c.archivee),
    suisAdmin: bool(c.suis_admin ?? c.is_admin),
    participants,
    membres,
    nbrParticipants: int(c.nbr_participants) || participants.length || membres?.length || 0,
    dernierMessage: dernier,
    date: c.dernier_message?.created_at ?? c.updated_at ?? null,
  };
}

// ══════════════════════════════════════════════════════
//  NOTIFICATION
// ══════════════════════════════════════════════════════
export function normNotif(n = {}) {
  n = n || {};
  return {
    id: int(n.id),
    type: n.type ?? 'systeme',
    titre: n.titre ?? '',
    contenu: n.contenu ?? n.message ?? '',
    lu: bool(n.lu ?? n.is_read),
    targetId: n.target_id != null ? int(n.target_id) : null,
    sender: n.sender ? normUser(n.sender) : null,
    date: n.created_at ?? null,
    dateTexte: n.temps_relatif ?? null,
  };
}